'use client';

import React, { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

interface GridProps {
  children: React.ReactNode;
  container?: boolean;
  item?: boolean;
  spacing?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12 | 16;
  direction?: 'row' | 'row-reverse' | 'column' | 'column-reverse';
  justifyContent?:
    | 'flex-start'
    | 'center'
    | 'flex-end'
    | 'space-between'
    | 'space-around'
    | 'space-evenly';
  alignItems?: 'flex-start' | 'center' | 'flex-end' | 'stretch' | 'baseline';
  wrap?: 'nowrap' | 'wrap' | 'wrap-reverse';
  className?: string;
  component?: React.ElementType;
  sizes?: Partial<Record<'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl', number>>;
}

const getGapClass = (value: number = 0): string => {
  const gapMap: Record<number, string> = {
    0: 'gap-0',
    1: 'gap-1',
    2: 'gap-2',
    3: 'gap-3',
    4: 'gap-4',
    5: 'gap-[1.25rem]',
    6: 'gap-6',
    8: 'gap-8',
    10: 'gap-10',
    12: 'gap-12',
    16: 'gap-16',
  };

  return gapMap[value] || '';
};

const getColClass = (colSize: number, prefix = ''): string => {
  if (!colSize || colSize < 1 || colSize > 12) return '';
  const pre = prefix ? `${prefix}:` : '';
  const perc = Math.round((colSize / 12) * 100);

  switch (colSize) {
    case 12:
      return `${pre}w-full`;
    case 6:
      return `${pre}w-1/2`;
    case 4:
      return `${pre}w-1/3`;
    case 3:
      return `${pre}w-1/4`;
    case 2:
      return `${pre}w-1/6`;
    case 1:
      return `${pre}w-1/12`;
    default:
      return `${pre}w-[${perc}%]`;
  }
};

const GridComponent: React.FC<GridProps> = ({
  children,
  container = false,
  item = false,
  spacing = 0,
  direction = 'row',
  justifyContent = '',
  alignItems = '',
  wrap = 'wrap',
  className = '',
  component: Component = 'div',
  sizes = {},
  ...other
}) => {
  const containerClass = useMemo(() => {
    if (!container) return '';
    return twMerge(
      'flex',
      `flex-${direction}`,
      ...(justifyContent ? [`justify-${justifyContent.replace('flex-', '')}`] : []),
      ...(alignItems ? [`items-${alignItems.replace('flex-', '')}`] : []),
      `flex-${wrap}`,
      getGapClass(spacing)
    );
  }, [container, direction, justifyContent, alignItems, wrap, spacing]);

  const itemClass = useMemo(() => {
    if (!item) return '';
    const classes: string[] = [];

    Object.entries(sizes).forEach(([breakpoint, val]) => {
      if (!val || val < 1 || val > 12) return;
      const prefix = breakpoint === 'xs' ? '' : breakpoint;
      classes.push(getColClass(val, prefix));
    });

    return twMerge(classes.join(' '));
  }, [item, sizes]);

  return (
    <Component className={twMerge(containerClass, itemClass, className)} {...other}>
      {children}
    </Component>
  );
};

export const Grid = React.memo(GridComponent);
export default Grid;
